import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, fromEvent, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import {
    ConsentCookie,
    ConsentStatus,
    ScriptSettings,
    SnippetSettings
} from '@core/services/cookie-consent/cookie-consent';
import { ConsentType } from '@core/services/cookie-consent/cookie-consent.config';
import { LoadScriptOptions } from '@core/services/load-scripts/load-scripts';
import { LoadScriptsService } from '@core/services/load-scripts/load-scripts.service';
import { PlatformService } from '@core/services/platform.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UniqueIdentifierService } from '@utils/unique-identifier.service';
import { ServerTokensService } from '@core/services/server-tokens.service';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class CookieConsentService {
    private cookieInformationUrl = 'https://policy.app.cookieinformation.com/uc.js';
    private cookieInformationOptions: LoadScriptOptions = {
        attributes: {
            'data-culture': 'DA',
            'data-gcm-version': '2.0'
        }
    };

    private scripts: {
        [key: string]: Promise<boolean>;
    } = {};
    private snippets: {
        [key: string]: Promise<boolean>;
    } = {};

    private consentEvent$ = this.getConsentEvent();
    private consentCookie$: Observable<ConsentCookie>;
    private consentCookieBs$: BehaviorSubject<ConsentCookie> = new BehaviorSubject<ConsentCookie>(
        this.getConsentCookie()
    );
    // Initialize consentStatus with cookie, if it exists
    private consentStatusBs$: BehaviorSubject<ConsentStatus> = new BehaviorSubject(
        this.mapCookieToStatus(this.getConsentCookie())
    );
    public consentStatus$: Observable<ConsentStatus> = this.consentStatusBs$.asObservable();

    constructor(
        private readonly cookieService: CookieService,
        private readonly loadscriptsService: LoadScriptsService,
        private readonly uniqueIdentifierService: UniqueIdentifierService,
        private readonly serverTokensService: ServerTokensService,
        private readonly platformService: PlatformService
    ) {
        // Consent cookie is the whole cookie. It is not used for anything but approved consents right now,
        // but plenty of more information is available in this if needed
        this.consentCookie$ = this.consentEvent$.pipe(
            map(() => this.getConsentCookie()),
            untilDestroyed(this),
            distinctUntilChanged()
        );
        // Subscribe to the consentCookie and get consentStatus from the contents of that cookie
        this.consentCookie$
            .pipe(
                map(consentCookie => this.mapCookieToStatus(consentCookie)),
                untilDestroyed(this)
            )
            .subscribe(consentStatus => this.consentStatusBs$.next(consentStatus));
    }

    public initCookieConsent(): void {
        if (this.serverTokensService.isBot || this.platformService.isServer) {
            return;
        }

        this.loadscriptsService.loadScript(this.cookieInformationUrl, this.cookieInformationOptions);
    }

    /**
     *  Adds a snippet to scripts object, with their id as object key.
     *
     *  snippet: SnippetSettings - The snippet to add to the scripts object
     *  Returns a promise, that will resolve when the consent type has been approved.
     * @param snippet
     */
    public addSnippetToLoadIfConsentGiven(snippet: SnippetSettings): Promise<boolean> {
        if (!(snippet.id in this.snippets)) {
            this.snippets[snippet.id] = new Promise<boolean>(resolve => {
                this.consentStatus$
                    .pipe(
                        filter(consentStatus => !!consentStatus[snippet.type]),
                        take(1),
                        untilDestroyed(this)
                    )
                    .subscribe(() => {
                        resolve(
                            this.loadscriptsService.loadSnippet(snippet.snippet, snippet.id, snippet.scriptOptions)
                        );
                    });
            });
        }

        return this.snippets[snippet.id];
    }

    /**
     * Adds scripts to scripts object, with their url as object key.
     * Returns a promise, that will resolve when the consent type has been approved.
     * If multiple sources try to load the same resource, they will share the same promise
     * @param script
     */
    public addScriptToLoadIfConsentGiven(script: ScriptSettings): Promise<boolean> {
        if (!(script.url in this.scripts)) {
            this.scripts[script.url] = new Promise<boolean>(resolve => {
                this.consentStatus$
                    .pipe(
                        filter(consentStatus => !!consentStatus[script.type]),
                        take(1),
                        untilDestroyed(this)
                    )
                    .subscribe(() => {
                        resolve(this.loadScript(script));
                    });
            });
        }
        return this.scripts[script.url];
    }

    public openCookieConsentPopup(): void {
        (window as any).CookieConsent?.renew();
    }

    /**
     * In case we are on an anonymous browser or secure browser, like brave, we don't have access the cookie information,
     * and probably will be a unique visit, so we will generate a GUID for that user
     */
    public getCookieConsentUUID(): Observable<string> {
        return this.consentCookieBs$.pipe(
            map(cookieInformation => this.uniqueIdentifierService.getGUID(cookieInformation))
        );
    }

    // Returns ConsentStatus object, with each ConsentType as boolean
    private mapCookieToStatus(consentCookie: ConsentCookie): ConsentStatus {
        const consentStatus: ConsentStatus = {};
        Object.entries(ConsentType).forEach(([_, value]) => {
            consentStatus[value] = consentCookie.consents_approved.indexOf(value) > -1;
        });
        return consentStatus;
    }

    private loadScript(script: ScriptSettings): Promise<boolean> {
        return this.loadscriptsService.loadScript(
            script.url,
            script.attributes ? { attributes: script.attributes } : undefined
        );
    }

    private getConsentCookie(): ConsentCookie {
        const cookieValue: string = this.cookieService.get('CookieInformationConsent');
        if (!cookieValue) {
            return {
                consents_approved: [],
                consents_denied: []
            };
        }

        // For some reason in iOS safari, the cookie is not decoded correctly, so we need to do some fallbacks to make it work
        // at this moment we are getting a lot of errors doing the parse, so we need to do some fallbacks
        try {
            const decodedCookieValue = decodeURIComponent(cookieValue);
            return JSON.parse(decodedCookieValue);
        } catch (error) {
            console.warn('Error parsing cookie', error);
            return this.getConsentFallback(cookieValue);
        }
    }

    // Fallback for parsing cookie yes... people will start thinking i'm crazy
    // but not... this is a real thing that is happening
    private getConsentFallback(cookieValue: string): ConsentCookie {
        try {
            return JSON.parse(cookieValue);
        } catch (error) {
            console.warn('Error parsing cookie so we will use fallback', error);
            return {
                consents_approved: [],
                consents_denied: []
            };
        }
    }

    private getConsentEvent(): Observable<Event> {
        if (this.platformService.isServer) {
            return of<Event>();
        }
        return fromEvent(window, 'CookieInformationConsentGiven');
    }
}
